import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';

import { NodePanelDropDown } from 'components/basic/NodePanelDropDown/NodePanelDropDown';
import { NodeDropDownSectionProps, NodeDropDownSectionValue } from 'types/rofl';
import { GeneralIssue, getGeneralIssue, getPropertyInputErrorMessage, getValidationError } from 'util/errors';
import useAbortController from 'hooks/useAbortController';
import { RequestError } from 'hooks/useRequest';
import { NodePanelSearchableDropdown } from 'components/basic/NodePanelDropDown/NodePanelSearchableDropdown';
import { InputErrorElement, InputType } from 'components/basic/inputProperty/InputErrorElement/InputErrorElement';

export const defaultDropDownSearch = (search: string, option: NodeDropDownSectionValue) => !search
        || (option.display_value || '').toLowerCase().includes(search.toLowerCase());

export const NodeDropDownSection = ({
    selected,
    onSelect,
    serviceTitle,
    entityTitle,
    dropdownTitle,
    data,
    allowRefresh,
    propertyError,
    multiple,
    onSearch,
    identifier,
    setIsValid,
    placeholder,
    isDisabled,
    actionPropertyValuesInput,
    shouldSearchBackend,
    hideLabel,
    selectedDisplayValue
}: NodeDropDownSectionProps) => {
    const getSignal = useAbortController();
    const [error, setError] = React.useState<React.ReactNode>();

    const isDataFcn = typeof data === 'function';
    const [loading, setLoading] = useState<boolean>(false);
    const [loadedData, setLoadedData] = useState<NodeDropDownSectionValue[]>();

    const onRefresh = useCallback(async (searchParam?: string) => {
        if (!isDataFcn) {
            return;
        }

        const abortSignal = getSignal();
        setLoading(true);
        const values = await data({
            onError: (requestError: RequestError) => {
                if (abortSignal.aborted) {
                    return true;
                }
                setError(getPropertyInputErrorMessage(requestError, entityTitle));
                setLoading(false);
                return true;
            },
            searchParam
        });
        if (!abortSignal.aborted) {
            setLoadedData(values);
            setLoading(false);
        }
    }, [entityTitle, data, getSignal, isDataFcn]);

    const setData = useCallback(async () => {
        if (isDisabled) {
            return;
        }
        if ((!!selected || selected === false) // boolean value 'false' is a valid field value
            && shouldSearchBackend && selectedDisplayValue) {
            setLoadedData([{ value: selected, display_value: selectedDisplayValue }]);
        } else {
            onRefresh();
        }

        // set any property errors if they exist
        if (propertyError?.error) {
            setError(getValidationError(propertyError.error, entityTitle));
            setIsValid((isValid: Record<string, boolean>) => {
                return { ...isValid, [identifier]: false };
            });
        }
    }, [entityTitle, identifier, isDisabled, onRefresh, propertyError?.error,
        selected, selectedDisplayValue, setIsValid, shouldSearchBackend]);

    // since we only want this to run once (on component mount), with no dependencies,
    // we use a useRef.
    const setInitialData = useRef(setData);
    useEffect(() => {
        setInitialData.current();
    }, []);

    // store dependent action Property Values in a ref so we can compare them to the previous dependencies
    const actionPropertyValuesInputRef = useRef(actionPropertyValuesInput);
    const actionPropertyValuesInputChanged = !_.isEqual(
        actionPropertyValuesInput, actionPropertyValuesInputRef.current);
    useEffect(() => {
        if (!isDataFcn) {
            setLoadedData(data);
        } else if (actionPropertyValuesInputChanged) {
            // refetch the data if find a change
            actionPropertyValuesInputRef.current = actionPropertyValuesInput;
            setData();
        }
    }, [data, isDataFcn, setData, actionPropertyValuesInput, actionPropertyValuesInputChanged]);


    const header = useMemo(() => {
        if ((loadedData || []).length <= 0) {
            return <p>{getGeneralIssue(GeneralIssue.NO_DATA, entityTitle, serviceTitle)}</p>;
        }
        return undefined;
    }, [loadedData, serviceTitle, entityTitle]);

    const NodePanelDropdownComponent
        = shouldSearchBackend ? NodePanelSearchableDropdown : NodePanelDropDown<NodeDropDownSectionValue>;

    const errorElement = (
        <InputErrorElement
            value={selected}
            inputType={InputType.DROPDOWN}
            error={error}
            propertyError={propertyError}
            setError={setError}
            setIsValid={setIsValid}
            shouldSetError={!selected}
            fieldIsValid={!!selected || selected === false} // boolean value 'false' is a valid field value
            errorClassName='workflow-node-panel-select-dropdown-error-msg'
            identifier={identifier}
            entityTitle={entityTitle}
            serviceTitle={serviceTitle}
        />
    );
    return (
        <>
            {!hideLabel && (
                <p className='workflow-node-panel-dropdown-title'>
                    {`${dropdownTitle || entityTitle}`}
                </p>
            )}
            <NodePanelDropdownComponent
                data={loadedData}
                placeholder={placeholder}
                multiple={multiple}
                label={multiple ? () => {
                    const matchedData = loadedData?.filter(optionData => selected.includes(optionData.value));
                    const dataDisplayValues = matchedData?.map(optionData => optionData.display_value).join(', ');
                    return dataDisplayValues || '';
                } : undefined} // TODO: make this customizable
                error={error}
                header={header}
                onSearch={onSearch}
                onSelect={(option, isOptionSelected, selectedOptions) => {
                    setError(undefined);
                    onSelect({
                        value: option.value,
                        display_value: option.display_value
                    }, isOptionSelected, selectedOptions);
                }}
                renderOption={option => option.display_value}
                renderSelected={option => {
                    if (multiple) {
                        return selected?.some((selectedOptionData: any) => {
                            return option.value === selectedOptionData;
                        });
                    }
                    return option.value === selected;
                }}
                allowRefresh={allowRefresh}
                loading={loading}
                isDisabled={isDisabled}
                onRefresh={onRefresh}
                errorElement={errorElement}
            />
        </>
    );
};
